import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Star, Crown } from 'lucide-react';
import { useSubscription } from '../lib/hooks/useSubscription';

interface SubscriptionBadgeProps {
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const SubscriptionBadge: React.FC<SubscriptionBadgeProps> = ({ 
  size = 'md',
  showLabel = true
}) => {
  const { currentSubscription, loading } = useSubscription();

  if (loading) return null;

  // Skip rendering for free plan
  if (currentSubscription.plan === 'free') return null;

  const getPlanIcon = () => {
    switch (currentSubscription.plan) {
      case 'charged':
        return <Zap className={size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'} />;
      case 'supercharged':
        return <Crown className={size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'} />;
      default:
        return <Star className={size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'} />;
    }
  };

  const getBadgeColors = () => {
    switch (currentSubscription.plan) {
      case 'charged':
        return 'bg-indigo-500 text-white';
      case 'supercharged':
        return 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white';
      default:
        return 'bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200';
    }
  };

  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-[10px]',
    md: 'px-2 py-1 text-xs',
    lg: 'px-2.5 py-1.5 text-sm'
  };

  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`rounded-full flex items-center space-x-1 ${getBadgeColors()} ${sizeClasses[size]}`}
    >
      {getPlanIcon()}
      {showLabel && (
        <span className="font-medium capitalize">
          {currentSubscription.plan}
        </span>
      )}
    </motion.div>
  );
};

export default SubscriptionBadge;